import { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { Search, Play, X, Clock, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { TV } from "@/constants/testIds";
import { useAudio } from "@/context/AudioContext";

const fmtDuration = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const VideoModal = ({ video, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!video || video.source_type === "youtube") return;
    if (!ref.current) return;
    const v = ref.current;
    const src = video.video_url;
    if (!src) return;

    const isHls = /\.m3u8($|\?)/i.test(src);
    if (!isHls) {
      v.src = src;
      return;
    }
    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = src;
    } else if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(v);
      return () => hls.destroy();
    }
  }, [video]);

  if (!video) return null;
  const isYT = video.source_type === "youtube" && video.youtube_id;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
    >
      <div
        className="w-full max-w-4xl bg-black rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video bg-black">
          {isYT ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0&modestbranding=1`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <video ref={ref} controls autoPlay playsInline className="w-full h-full" />
          )}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 sm:p-5 bg-[var(--fi-blue)] text-white">
          <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold">
            {video.program}
          </div>
          <div className="font-display text-xl sm:text-2xl font-extrabold uppercase">
            {video.title}
          </div>
          <p className="text-sm text-white/70 mt-2">{video.description}</p>
        </div>
      </div>
    </div>
  );
};

const TVLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);
  const { stop: stopAudio } = useAudio();

  useEffect(() => {
    const params = {};
    if (category !== "all") params.category = category;
    if (q.trim()) params.q = q.trim();
    api.get("/videos", { params }).then((r) => setVideos(r.data)).catch(() => {});
  }, [q, category]);

  const categories = useMemo(() => {
    const set = new Set(videos.map((v) => v.category));
    return ["all", ...Array.from(set)];
  }, [videos]);

  const openVideo = (v) => {
    stopAudio();
    setSelected(v);
  };

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
        TV · Biblioteca
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-6">
        Vídeos gravados
      </h1>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          data-testid={TV.librarySearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar vídeos..."
          className="w-full rounded-xl bg-white pl-11 pr-4 py-3 text-sm outline-none border border-gray-200 focus:border-[var(--fi-blue)] transition-colors"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4 -mx-1 px-1">
        {categories.map((c) => {
          const active = category === c;
          return (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-bold whitespace-nowrap transition-colors ${
                active
                  ? "bg-[var(--fi-blue)] text-white"
                  : "bg-white text-gray-600 hover:text-[var(--fi-blue)] border border-gray-200"
              }`}
            >
              {c === "all" ? "Todos" : c}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {videos.map((v) => (
          <article
            key={v.id}
            data-testid={TV.libraryCard(v.id)}
            className="group rounded-xl bg-white overflow-hidden shadow-[0_6px_24px_rgba(13,27,75,0.06)] hover:shadow-[0_12px_36px_rgba(13,27,75,0.12)] transition-shadow cursor-pointer"
            onClick={() => openVideo(v)}
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={v.thumbnail_url}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-2 right-2 text-[11px] text-white font-bold bg-black/60 px-2 py-0.5 rounded">
                {fmtDuration(v.duration_seconds)}
              </div>
              <button
                data-testid={TV.libraryPlay(v.id)}
                className="absolute inset-0 flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  openVideo(v);
                }}
                aria-label={`Assistir ${v.title}`}
              >
                <span className="w-14 h-14 rounded-full bg-[var(--fi-red)] text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 ml-1" />
                </span>
              </button>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--fi-red)]">
                {v.program}
              </div>
              <div className="font-display text-lg font-extrabold text-[var(--fi-blue)] leading-tight line-clamp-2 mt-0.5">
                {v.title}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                <span className="inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {v.views.toLocaleString("pt-BR")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {fmtDuration(v.duration_seconds)}
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>

      {videos.length === 0 && (
        <div className="text-center py-16 text-gray-400 font-medium">
          Nenhum vídeo encontrado.
        </div>
      )}

      <VideoModal video={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

export default TVLibrary;
